import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faFileLines, faEquals } from '@fortawesome/free-solid-svg-icons';

// Tiny hand-built "what you get" previews per premium feature — shown inside
// the UpgradeModal instead of an abstract gradient placeholder, so the
// locked feature has a concrete face.

const Frame = ({ children }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">{children}</div>
);

const sefa = (
  <Frame>
    <div className="flex items-center justify-center gap-2 text-xs">
      <span className="rounded-lg bg-white border border-slate-200 px-2 py-1 font-mono text-slate-700">
        SEE 1.92
      </span>
      <span className="text-slate-400">−</span>
      <span className="rounded-lg bg-primaryColor/10 border border-primaryColor/30 px-2 py-1 font-mono text-primaryColor font-semibold">
        SEFA 1.248
      </span>
      <FontAwesomeIcon
        icon={faEquals}
        className="text-slate-400 text-[10px]"
      />
      <span className="rounded-lg bg-emerald-50 border border-emerald-200 px-2 py-1 font-mono text-emerald-700 font-semibold">
        0.672 chargeable
      </span>
    </div>
    <p className="text-[10px] text-slate-400 text-center m-0 mt-2">
      Calculated per good from your fuels, electricity and precursors.
    </p>
  </Frame>
);

const verification = (
  <Frame>
    <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 flex items-center gap-3">
      <span className="font-mono text-[10px] text-slate-500">72081000</span>
      <span className="text-xs text-slate-600 flex-1">Hot-rolled flat steel · 2026</span>
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
        <FontAwesomeIcon
          icon={faCircleCheck}
          className="text-[9px]"
        />
        Verified
      </span>
    </div>
    <p className="text-[10px] text-slate-400 text-center m-0 mt-2">
      The badge your customers see on your portal after verification.
    </p>
  </Frame>
);

const insights = (
  <Frame>
    <div className="flex items-end justify-center gap-1.5 h-14">
      {[35, 55, 40, 70, 60, 85, 78].map((h, i) => (
        <div
          key={i}
          className="w-5 rounded-t bg-primaryColor/70"
          style={{ height: `${h}%` }}
        />
      ))}
    </div>
    <p className="text-[10px] text-slate-400 text-center m-0 mt-2">
      Portal views, data requests and emissions intensity over time.
    </p>
  </Frame>
);

const costs = (
  <Frame>
    <div className="flex flex-col gap-1 text-[11px] font-mono">
      <div className="flex justify-between text-slate-600">
        <span>Chargeable emissions</span>
        <span>0.672 tCO₂e/t</span>
      </div>
      <div className="flex justify-between text-slate-600">
        <span>Certificate price</span>
        <span>× €72.00</span>
      </div>
      <div className="flex justify-between font-semibold text-slate-800 border-t border-slate-200 pt-1">
        <span>CBAM cost</span>
        <span>€48.38 /t</span>
      </div>
    </div>
    <p className="text-[10px] text-slate-400 text-center m-0 mt-2">
      Live per-good cost breakdowns with certificate price tracking.
    </p>
  </Frame>
);

const compliance = (
  <Frame>
    <div className="flex flex-col gap-1.5">
      {[
        'Monitoring plan — Izmir Plant',
        'Emissions report 2026',
        'Communication template export',
      ].map((doc) => (
        <div
          key={doc}
          className="rounded-lg bg-white border border-slate-200 px-2.5 py-1.5 flex items-center gap-2 text-[11px] text-slate-600"
        >
          <FontAwesomeIcon
            icon={faFileLines}
            className="text-slate-400 text-[10px]"
          />
          {doc}
          <FontAwesomeIcon
            icon={faCircleCheck}
            className="text-emerald-500 text-[10px] ml-auto"
          />
        </div>
      ))}
    </div>
    <p className="text-[10px] text-slate-400 text-center m-0 mt-2">
      Generated from the data you've already entered — no re-typing.
    </p>
  </Frame>
);

const UPGRADE_PREVIEWS = {
  sefa,
  verification,
  insights,
  costs,
  compliance,
  generic: insights,
};

export default UPGRADE_PREVIEWS;
